import { coerce } from "gamla";
import { type JsonData, JsonEditor } from "json-edit-react";
import { type JsonSchema, jsonSchemaToZod } from "json-schema-to-zod";
import { useEffect, useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import type { RemoteSkill, RemoteTool } from "../../instant.schema.ts";
import {
  BotNotFound,
  Button,
  Collapsible,
  inlineCodeClass,
  inputClass,
  labelClass,
  PageSpinner,
} from "./components.tsx";
import { transact, tx, useQuery } from "./db.ts";

const emptyTool = (): RemoteTool => ({
  name: "",
  description: "",
  url: "",
  parameters: { type: "object", properties: {}, required: [] },
});

const emptySkill = (): RemoteSkill => ({
  name: "",
  description: "",
  instructions: "",
  tools: [],
});

const ToolEditor = (
  { tool, onUpdate }: {
    tool: RemoteTool;
    onUpdate: (patch: Partial<RemoteTool>) => void;
  },
) => (
  <div className="flex flex-col gap-3 border border-border-subtle-light dark:border-border-subtle rounded p-3">
    <div>
      <label className={labelClass}>Name</label>
      <input
        className={inputClass}
        value={tool.name}
        onInput={(e) => onUpdate({ name: e.currentTarget.value })}
        placeholder="summarize_doc"
      />
    </div>
    <div>
      <label className={labelClass}>Description</label>
      <textarea
        className={inputClass}
        rows={2}
        value={tool.description}
        onInput={(e) => onUpdate({ description: e.currentTarget.value })}
        placeholder="Summarize a document at a given URL"
      />
    </div>
    <div>
      <label className={labelClass}>URL</label>
      <input
        className={inputClass}
        value={tool.url}
        onInput={(e) => onUpdate({ url: e.currentTarget.value })}
        placeholder="https://example.com/tools/summarize"
      />
    </div>
    <div>
      <label className={labelClass}>JSON Schema (parameters)</label>
      <div className="border border-border-default-light dark:border-border-default rounded overflow-hidden relative">
        <JsonEditor
          data={tool.parameters}
          setData={(parameters: JsonData) =>
            onUpdate({ parameters: parameters as JsonSchema })}
        />
      </div>
      <div>
        <div>Result:</div>
        <code className={inlineCodeClass}>
          {jsonSchemaToZod(tool.parameters)}
        </code>
      </div>
      <div className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-1">
        Must be a valid JSON schema for the tool parameters.
      </div>
    </div>
  </div>
);

const SkillEditor = (
  { skill, onUpdate }: {
    skill: RemoteSkill;
    onUpdate: (patch: Partial<RemoteSkill>) => void;
  },
) => {
  const updateTool = (idx: number, patch: Partial<RemoteTool>) =>
    onUpdate({
      tools: skill.tools.map((t, i) =>
        i === idx ? { ...t, ...patch } as RemoteTool : t
      ),
    });

  const addTool = () => onUpdate({ tools: [...skill.tools, emptyTool()] });

  const removeTool = (idx: number) =>
    onUpdate({ tools: skill.tools.filter((_, i) => i !== idx) });

  return (
    <div className="flex flex-col gap-3">
      <div>
        <label className={labelClass}>Skill Name</label>
        <input
          className={inputClass}
          value={skill.name}
          onInput={(e) => onUpdate({ name: e.currentTarget.value })}
          placeholder="crm_integration"
        />
      </div>
      <div>
        <label className={labelClass}>Description</label>
        <textarea
          className={inputClass}
          rows={2}
          value={skill.description}
          onInput={(e) => onUpdate({ description: e.currentTarget.value })}
          placeholder="Interact with the CRM system"
        />
      </div>
      <div>
        <label className={labelClass}>Instructions</label>
        <textarea
          className={inputClass}
          rows={3}
          value={skill.instructions}
          onInput={(e) => onUpdate({ instructions: e.currentTarget.value })}
          placeholder="Use this skill to query and update CRM records..."
        />
      </div>
      <div className="flex justify-between items-center mt-2">
        <div className="font-medium text-sm">Tools</div>
        <Button onClick={addTool} className="px-2 py-1 text-sm">
          + Add Tool
        </Button>
      </div>
      {skill.tools.length === 0 && (
        <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
          No tools in this skill yet
        </div>
      )}
      {skill.tools.map((t, i) => (
        <div key={i} className="flex flex-col gap-1">
          <ToolEditor tool={t} onUpdate={(patch) => updateTool(i, patch)} />
          <Button
            onClick={() => removeTool(i)}
            className="bg-danger hover:bg-danger-hover text-white self-end px-2 py-1 text-xs"
          >
            Remove Tool
          </Button>
        </div>
      ))}
    </div>
  );
};

export const CustomSkillsConfig = ({ id }: { id: string }) => {
  const { data, error, isLoading } = useQuery({
    bots: { $: { where: { id } } },
  });
  const [skillsDraft, setSkillsDraft] = useState<RemoteSkill[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!isLoading && !error) {
      const [bot] = data.bots;
      if (bot?.remoteSkills) setSkillsDraft(bot.remoteSkills as RemoteSkill[]);
    }
  }, [isLoading, error, data]);

  if (isLoading) return <PageSpinner />;
  if (error) return <div>Error: {error.message}</div>;
  const [bot] = data.bots;
  if (!bot) return <BotNotFound />;

  const selectedSkill = selectedIndex != null
    ? skillsDraft[selectedIndex]
    : null;

  const updateSkill = (idx: number, patch: Partial<RemoteSkill>) =>
    setSkillsDraft((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], ...patch } as RemoteSkill;
      return copy;
    });

  const addSkill = () => {
    setSkillsDraft((prev) => [...prev, emptySkill()]);
    setSelectedIndex(skillsDraft.length);
  };

  const removeSkill = (idx: number) => {
    setSkillsDraft((prev) => prev.filter((_, i) => i !== idx));
    setSelectedIndex(null);
  };

  const hasInvalidSkill = skillsDraft.some(
    (s) => !s.name || s.tools.some((t) => !t.name || !t.url),
  );

  return (
    <Collapsible title="Custom Skills">
      <div className="flex flex-col gap-4">
        <div className="text-sm text-text-secondary-light dark:text-text-secondary">
          Group remote tools into skills. Skills are loaded on demand by the
          agent when relevant, which helps keep context focused. Each tool
          within a skill works the same as a custom tool — an HTTP endpoint
          called with the same webhook secret.
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="sm:w-1/3 w-full border border-border-default-light dark:border-border-default rounded p-2 flex flex-col gap-2 bg-surface-raised-light dark:bg-surface-raised">
            <div className="flex justify-between items-center mb-1">
              <div className="font-medium">Skills</div>
              <Button onClick={addSkill} className="px-2 py-1 text-sm">
                + Add
              </Button>
            </div>
            {skillsDraft.length === 0 && (
              <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
                No skills yet
              </div>
            )}
            <ul className="flex-1 overflow-y-auto flex flex-col gap-1">
              {skillsDraft.map((s, i) => {
                const active = selectedIndex === i;
                return (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => setSelectedIndex(i)}
                      className={`w-full text-left px-2 py-1 rounded border text-sm truncate transition ${
                        active
                          ? "bg-accent text-white border-accent"
                          : "bg-surface-overlay-light dark:bg-surface-overlay border-border-default-light dark:border-border-default hover:bg-surface-overlay-light dark:hover:bg-surface-overlay"
                      }`}
                      title={s.name || "(unnamed)"}
                    >
                      {s.name || (
                        <span className="italic text-text-tertiary-light dark:text-text-tertiary">
                          (unnamed)
                        </span>
                      )}
                      <span className="ml-1 text-xs opacity-60">
                        ({s.tools.length} tools)
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {selectedIndex != null && (
              <Button
                onClick={() => removeSkill(coerce(selectedIndex))}
                className="bg-danger hover:bg-danger-hover text-white mt-2"
              >
                Remove Selected
              </Button>
            )}
            <Button
              onClick={() =>
                toast.promise(
                  transact(
                    tx.bots[id].update({ remoteSkills: skillsDraft }),
                  ),
                  {
                    loading: "Saving...",
                    success: "Saved",
                    error: (e: Error) => `Error: ${e.message}`,
                  },
                )}
              disabled={hasInvalidSkill}
              className="mt-2"
            >
              Save Skills
            </Button>
          </div>
          <div className="sm:w-2/3 w-full border border-border-default-light dark:border-border-default rounded p-4 flex flex-col gap-3 bg-surface-raised-light dark:bg-surface-raised min-h-[400px] overflow-y-auto">
            {selectedSkill
              ? (
                <SkillEditor
                  skill={selectedSkill}
                  onUpdate={(patch) =>
                    updateSkill(coerce(selectedIndex), patch)}
                />
              )
              : (
                <div className="text-sm text-text-tertiary-light dark:text-text-tertiary">
                  Select a skill to edit
                </div>
              )}
          </div>
        </div>
      </div>
    </Collapsible>
  );
};
